import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Users, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { services } from '../mock';
import MainServiceCard from '../components/sections/MainServiceCard';
import ServiceProcessCard from '../components/sections/ServiceProcessCard';
import ValueCard from '../components/sections/ValueCard';
import PageMeta from '../components/sections/PageMeta';

const SERVICE_IMAGES = [
  'https://images.unsplash.com/photo-1758691462858-f1286e5daf40',
  'https://images.unsplash.com/photo-1758691461935-202e2ef6b69f',
  'https://images.unsplash.com/photo-1758691462666-6470b740f544',
];

const SERVICE_PHASES = [
  {
    title: 'Before the Appointment',
    items: [
      'Review medical history and current concerns',
      'Prepare list of questions for the doctor',
      'Coordinate transportation if needed',
      'Gather all necessary medical records',
    ],
  },
  {
    title: 'During the Appointment',
    items: [
      'Accompany you to the examination room',
      `Take detailed notes of doctor's instructions`,
      'Ask clarifying questions on your behalf',
      'Ensure all concerns are addressed',
    ],
  },
  {
    title: 'After the Appointment',
    items: [
      `Review and explain doctor's instructions`,
      'Arrange for a follow-up call if necessary',
      'Discuss information sharing with family members',
      'Ensure you get safely into your transportation',
    ],
  },
];

const ADDITIONAL_SERVICES = [
  {
    icon: Clock,
    title: 'Flexible Scheduling',
    description: 'Available for appointments at times that work best for you and your family',
  },
  {
    icon: Users,
    title: 'Family Communication',
    description: 'Regular updates to family members about appointments and care plans',
  },
  {
    icon: Heart,
    title: 'Ongoing Support',
    description: 'Available to answer questions and provide guidance between appointments',
  },
];

const Services = () => (
  <div className="min-h-screen">
    <PageMeta
      title="Nurse Companion Services for Seniors | Doctor Visit Support in Wake County NC"
      description="A registered nurse accompanies seniors to doctor appointments, asks the right questions, and translates medical information. Serving Fuquay Varina, Holly Springs, Apex, and Cary, NC."
      path="/services"
    />
    <section className="bg-gradient-to-br from-blue-50 to-white py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto text-center">
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
          Our Services
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Comprehensive nursing companion services designed to support you through every step of your healthcare journey
        </p>
      </div>
    </section>

    <section className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 gap-12">
          {services.map((service, index) => (
            <MainServiceCard
              key={service.id}
              service={service}
              index={index}
              image={SERVICE_IMAGES[index]}
            />
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            How We Support You Through Every Appointment
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our comprehensive approach ensures you&apos;re never alone or confused during medical visits
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {SERVICE_PHASES.map((phase, idx) => (
            <ServiceProcessCard key={phase.title} phase={phase} stepNumber={idx + 1} />
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-12">
          Additional Support Services
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {ADDITIONAL_SERVICES.map((item) => (
            <div key={item.title} className="border-2 rounded-xl">
              <ValueCard icon={item.icon} title={item.title} description={item.description} />
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
      <div className="max-w-4xl mx-auto text-center space-y-8">
        <h2 className="text-3xl sm:text-4xl font-bold">
          Ready to Experience Compassionate Care?
        </h2>
        <p className="text-xl text-blue-100">
          Let us help you navigate your healthcare journey with confidence and peace of mind
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/contact">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 text-lg px-8 py-6 w-full sm:w-auto">
              Book Your Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  </div>
);

export default Services;
